import { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { RangeDatePicker } from "@y0c/react-datepicker";

import Notification from "./Notification";
import RefundsTable from "./RefundsTable";
import useRefunds from "./UseRefunds";
import {
  USER_ROLE_EMPLOYEE,
  USER_ROLE_FINANCE,
  USER_ROLE_SUPERAGENT,
} from "../../common/constants";
import { toast } from "react-toastify";
import StatCard from "./StatCard";
import { XIcon } from "@heroicons/react/solid";
import { DateSwitcher } from "../../Pages/Agents/components/DateSwitcher";

const HalfRefund = () => {
  const { user } = useSelector((state) => state.auth);
  const [notification, setNotification] = useState({ message: "", type: "" });
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [rangeDate, setRangeDate] = useState([]);
  const [durationSelect, setDurationSelect] = useState("");

  const {
    refunds,
    loading,
    error,
    updateRefundStatus,
    refundBooking,
    fetchRefunds,
  } = useRefunds();

  const { startDate, endDate } = useMemo(
    () =>
      DateSwitcher({
        durationSelect,
        rangeDate,
      }),
    [durationSelect, rangeDate]
  );

  useEffect(() => {
    fetchRefunds({ searchTerm: "", status: "", startDate: "", endDate: "" });
  }, [fetchRefunds]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 1000);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (
      debouncedSearchTerm !== "" ||
      statusFilter !== "" ||
      startDate ||
      endDate
    ) {
      fetchRefunds({
        searchTerm: debouncedSearchTerm,
        status: statusFilter,
        startDate: startDate ? startDate?.toISOString() : "",
        endDate: endDate ? endDate?.toISOString() : "",
      });
    }
  }, [debouncedSearchTerm, statusFilter, startDate, endDate, fetchRefunds]);

  useEffect(() => {
    if (rangeDate?.[1] !== undefined) {
      setDurationSelect("Custom");
    }
  }, [rangeDate]);

  const query = useMemo(() => {
    return {
      startDate: startDate ? startDate?.toISOString() : "",
      endDate: endDate ? endDate?.toISOString() : "",
    };
  }, [startDate, endDate]);

  const handleStatusUpdate = async (bookingId, newStatus) => {
    const result = await updateRefundStatus(bookingId, newStatus);
    setNotification({
      message: result.success ? "Status updated successfully!" : result.message,
      type: result.success ? "success" : "error",
    });
  };

  const handleRefundClick = async (bookingId) => {
    const result = await refundBooking(bookingId);
    setNotification({
      message: result.success
        ? "Refund request sent successfully!"
        : result.message,
      type: result.success ? "success" : "error",
    });
  };

  const handleClearFilters = useCallback(() => {
    setSearchTerm("");
    setStatusFilter("");
    setDebouncedSearchTerm("");
    setRangeDate([]);
    setDurationSelect("");
    fetchRefunds({ searchTerm: "", status: "", startDate: "", endDate: "" });
  }, [fetchRefunds]);

  const handleDateChange = useCallback((start, end) => {
    setRangeDate([start, end]);
    setDurationSelect("Custom");
  }, []);

  const isSuper = [
    USER_ROLE_SUPERAGENT,
    USER_ROLE_EMPLOYEE,
    USER_ROLE_FINANCE,
  ].includes(user?.role);

  if (!isSuper) {
    toast.error("You are not authorized to access this page");
    window.location.href = "/booking";
    return;
  }

  const aggregateData = refunds?.aggregate?.[0];

  return (
    <div className="p-4 min-h-screen bg-gray-100 sm:p-6 lg:p-8">
      <Notification
        message={notification.message}
        type={notification.type}
        onDismiss={() => setNotification({ message: "", type: "" })}
      />
      <div className="mx-auto max-w-7xl">
        <header className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800">
            Half Refund Requests
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            A centralized place to manage all half refund bookings.
          </p>
        </header>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 gap-6 mb-8 md:grid-cols-2 lg:grid-cols-4">
          <StatCard aggregateData={aggregateData} />
        </div>

        <div className="flex gap-4 mb-4">
          <input
            type="text"
            placeholder="Search by PNR..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-4 py-2 w-full rounded-md border border-gray-300 md:w-1/3 focus:ring-blue-500 focus:border-blue-500"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 rounded-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Statuses</option>
            <option value="confirmed">Confirmed</option>
            <option value="initiated">Initiated</option>
            <option value="refund-done">Refunded</option>
          </select>

          <div className="px-0 mt-3 col-xl-5 col-xxl-3 mt-sm-0 mb-xxl-0">
            <RangeDatePicker
              startText="Start"
              endText="End"
              startPlaceholder="Start Date"
              endPlaceholder="End Date"
              value={rangeDate}
              onChange={handleDateChange}
              size="small"
              showMonthCnt={1}
              disableDay={(date) =>
                date > new Date() || date < new Date("2022-01-01")
              }
            />
          </div>
          {(searchTerm !== "" ||
            statusFilter !== "" ||
            rangeDate.length > 0) && (
            <button
              onClick={handleClearFilters}
              className="px-3 text-sm text-red-500 rounded-md border-red-500 border-1 focus:ring-red-500 focus:border-red-500"
            >
              <div className="flex flex-row gap-2 items-center text-xs">
                <span className="sr-only">Clear</span>
                <XIcon className="w-3 h-3" />
                Clear
              </div>
            </button>
          )}
        </div>
        <div className="overflow-hidden bg-white rounded-lg shadow-md">
          {loading ? (
            <div className="p-12 text-center">Loading refunds...</div>
          ) : error ? (
            <div className="p-12 text-center text-red-500">{error}</div>
          ) : refunds.length === 0 ? (
            <div className="p-12 text-center">
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No refunds found
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                There are no refunds matching your criteria.
              </p>
            </div>
          ) : (
            <RefundsTable
              refunds={refunds?.bookings}
              onStatusUpdate={handleStatusUpdate}
              onRefundClick={handleRefundClick}
              query={query}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default HalfRefund;
